import CancelIcon from '@mui/icons-material/Cancel';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';

import React, { useContext, useState } from 'react';

import { ApiContext } from '_lib/api/apiContext';
import { AppConfigDetailDTO, AppConfigUpdateDTO, pick } from '_lib/api/apiTypes.shim';
import { BackdropSpinner } from '_lib/components/BackdropSpinner/BackdropSpinner';
import { Button } from '_lib/components/Button/Button';
import { Divider } from '_lib/components/Divider/Divider';
import { ErrorDisplay } from '_lib/components/ErrorDisplay/ErrorDisplay';
import { FieldSet } from '_lib/components/FieldSet/FieldSet';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { Stack } from '_lib/components/Stack/Stack';
import { TextField } from '_lib/components/TextField/TextField';
import { useOurSnackbar } from '_lib/utils/snackUtils';

type AppConfigManagementProps = {
  appConfigDetail: AppConfigDetailDTO;
};

export const AppConfigManagement = (props: AppConfigManagementProps) => {
  const { appConfigDetail } = props;

  const [isEditing, setIsEditing] = useState(false);
  const [appConfigUpdateDTODraft, setAppConfigUpdateDTODraft] = useState<string | null>(null);
  const [errors, setErrors] = useState<string | null>(null);

  const { enqueueSnackbar } = useOurSnackbar();

  const api = useContext(ApiContext);

  const appConfigUpdateMutation = api.appConfig.useUpdateAppConfig();

  const appConfigSubset = pick(appConfigDetail, ['id', 'modifiedAt', 'companyAgreements', 'companyConfigItems']);

  return (
    <FlexBox
      sx={{
        flexDirection: 'column',
        maxHeight: '700px',
        width: '100%',
        overflow: 'hidden',
        position: 'relative'
      }}>
      <FlexBox
        sx={{
          width: '100%',
          position: 'relative',
          flexDirection: 'column',
          overflow: 'hidden',
          padding: 2
        }}>
        <BackdropSpinner open={appConfigUpdateMutation.isPending} />
        <FieldSet
          legendText='App Config'
          sx={{ padding: 2, overflow: 'hidden', width: '100%', height: '100%', fontSize: 'medium' }}>
          <FlexBox sx={{ overflow: 'auto', width: '100%', height: '100%' }}>
            {!isEditing && <pre style={{ margin: 0 }}>{JSON.stringify(appConfigSubset, null, 4)}</pre>}
            {isEditing && (
              <TextField
                fullWidth
                multiline
                size='small'
                rows={20}
                value={appConfigUpdateDTODraft}
                onChange={(e) => setAppConfigUpdateDTODraft(e.target.value)}
              />
            )}
          </FlexBox>
        </FieldSet>
        {errors && <ErrorDisplay errors={[errors]} sx={{ borderRadius: 1 }} />}
      </FlexBox>
      <Divider />
      <FlexBox sx={{ width: '100%', height: '48px' }}>
        {!isEditing && (
          <Button
            startIcon={<EditIcon />}
            text='Edit'
            variant='contained'
            sx={{ textTransform: 'none', marginLeft: 'auto', marginY: 1, marginRight: 1 }}
            onClick={() => {
              setIsEditing(true);
              setAppConfigUpdateDTODraft(
                JSON.stringify(
                  {
                    companyAgreements: (appConfigSubset.companyAgreements || []).map((obj) => ({
                      companyId: obj.companyId,
                      companyName: obj.companyName,
                      agreementAppUrl: obj.agreementAppUrl
                    })),
                    companyConfigItems: (appConfigSubset.companyConfigItems || []).map((obj) => ({
                      companyId: obj.companyId,
                      companyName: obj.companyName,
                      jobTypeInfoUrl: obj.jobTypeInfoUrl,
                      isCallPopShellCompany: obj.isCallPopShellCompany
                    }))
                  },
                  null,
                  4
                )
              );
            }}
          />
        )}
        {isEditing && (
          <FlexBox sx={{ flexDirection: 'row', width: '100%' }}>
            <Button
              startIcon={<ContentCopyIcon />}
              color='primary'
              disabled={appConfigUpdateMutation.isPending}
              text='Copy to Clipboard'
              variant='contained'
              sx={{ textTransform: 'none', margin: 1 }}
              onClick={async () => {
                await navigator.clipboard.writeText(appConfigUpdateDTODraft || '');
                enqueueSnackbar('App Config Copied');
              }}
            />
            <Stack useFlexGap direction='row' spacing={1} sx={{ marginLeft: 'auto', marginY: 1, marginRight: 1 }}>
              <Button
                startIcon={<CancelIcon />}
                color='error'
                disabled={appConfigUpdateMutation.isPending}
                text='Cancel'
                variant='contained'
                sx={{ textTransform: 'none' }}
                onClick={() => {
                  setIsEditing(false);
                  setErrors(null);
                }}
              />
              <Button
                startIcon={<SaveIcon />}
                color='primary'
                disabled={appConfigUpdateMutation.isPending}
                text='Save'
                variant='contained'
                sx={{ textTransform: 'none' }}
                onClick={async () => {
                  let data: AppConfigUpdateDTO;
                  try {
                    setErrors(null);
                    data = JSON.parse(appConfigUpdateDTODraft || '');
                    await appConfigUpdateMutation.put({ data, url: '' });
                    enqueueSnackbar('App Config Saved');
                    setIsEditing(false);
                  } catch (err) {
                    const errorMessage = err instanceof Error ? err.message : 'Unexpected error parsing JSON';
                    setErrors(JSON.stringify([errorMessage], null, 4));
                    return;
                  }
                }}
              />
            </Stack>
          </FlexBox>
        )}
      </FlexBox>
    </FlexBox>
  );
};
